import axios from "axios";
import { useEffect, useRef, useState } from "react";
import { FileService } from "../../../services/FileService";

export interface AuthenticatedFileBlob {
  blobUrl: string | null;
  isLoading: boolean;
  hasError: boolean;
}

type Variant = "preview" | "original";

/**
 * Loads a file from the file service as an object URL.
 *
 * The file service requires an Authorization header, so the URL cannot be used
 * directly as an `<img src>` — the bytes have to be fetched and wrapped in a blob URL.
 *
 * When `variant` is "preview" and no preview rendition exists (common for attachments
 * uploaded from the app), the request fails; the original file is then fetched instead
 * so the image still renders. Any failure ends in `hasError` — callers must never keep
 * a spinner on screen based on `blobUrl` alone, or a failed request spins forever.
 */
export function useAuthenticatedFileBlob(
  idExternalFile: string | undefined,
  variant: Variant = "preview",
): AuthenticatedFileBlob {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(!!idExternalFile);
  const [hasError, setHasError] = useState(!idExternalFile);
  const blobUrlRef = useRef<string | null>(null);

  useEffect(() => {
    if (!idExternalFile) {
      setBlobUrl(null);
      setIsLoading(false);
      setHasError(true);
      return;
    }

    let canceled = false;

    const revokeCurrent = () => {
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
    };

    const fetchVariant = (v: Variant) =>
      axios.get<Blob>(FileService.getFileURL(idExternalFile, v, ""), {
        responseType: "blob",
      });

    revokeCurrent();
    setBlobUrl(null);
    setIsLoading(true);
    setHasError(false);

    const request =
      variant === "preview"
        ? fetchVariant("preview").catch(() => fetchVariant("original"))
        : fetchVariant("original");

    request
      .then((res) => {
        if (canceled) return;
        const url = URL.createObjectURL(res.data);
        blobUrlRef.current = url;
        setBlobUrl(url);
      })
      .catch(() => {
        if (!canceled) setHasError(true);
      })
      .finally(() => {
        if (!canceled) setIsLoading(false);
      });

    return () => {
      canceled = true;
      revokeCurrent();
    };
  }, [idExternalFile, variant]);

  return { blobUrl, isLoading, hasError };
}
